import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
// import { feeGroup, feesTypes, paymentType } from '../../../core/common/selectoption/selectoption'
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { all_routes } from "../../../router/all_routes";
import { status } from "../../../../core/common/selectoption/selectoption";
import CommonSelect from "../../../../core/common/commonSelect";
import { useSession } from "../hooks/useSession";
import { showToast } from "../../../../core/utils/alert";
import { SessionPayloadType } from "../types/sessionRequest";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const validationSchema = yup.object().shape({
  name: yup.string().required("Session name is required"),
  start_date: yup
    .mixed()
    .required("Start date is required")
    .test("is-date", "Invalid start date", (value) => dayjs(value as string).isValid()),
  end_date: yup
    .mixed()
    .required("End date is required")
    .test("is-date", "Invalid end date", (value) => dayjs(value as string).isValid())
    .test(
      "is-after-start",
      "End date must be after start date",
      function (value) {
        return dayjs(value as string).isAfter(this.parent.start_date);
      }
    ),
  status: yup.string().required("Status is required"),
  description: yup.string().required("Description is required"),
});

const AddSession = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const {
    addNewSession,
    updateSession,
    isAdding,
    isUpdating,
    addError,
    useGetSessionById,
  } = useSession();
  const { id } = useParams<{ id: string }>();
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, touchedFields },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      start_date: undefined,
      end_date: undefined,
      status: "",
      description: "",
    },
  });

  const sessionData = useGetSessionById(id || "");

  useEffect(() => {
    if (id && sessionData) {
      reset({
        name: sessionData.name || "",
        start_date: sessionData.start_date ? dayjs(sessionData.start_date) : undefined,
        end_date: sessionData.end_date ? dayjs(sessionData.end_date) : undefined,
        status: sessionData.status || "",
        description: sessionData.description || "",
      });
      setIsEdit(true);
    } else {
      reset();
      setIsEdit(false);
    }
  }, [id, sessionData, reset]);

  const onSubmit = async (data: any) => {
    const payload = {
      ...data,
      start_date: data.start_date?.format("YYYY-MM-DD") || "",
      end_date: data.end_date?.format("YYYY-MM-DD") || "",
    };

    const cleanPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v !== undefined)
    ) as unknown as SessionPayloadType;

    try {
      let result;
      if (isEdit && id) {
        result = await updateSession(id, cleanPayload);
      } else {
        result = await addNewSession(cleanPayload);
      }

      if (result?.success) {
        showToast({
          title: "Success",
          text: `Session ${isEdit ? "updated" : "added"} successfully.`,
          icon: "success",
          timer: 5000,
        });
        navigate(routes.sessionList);
      } else {
        showToast({
          title: "Error",
          text: String(
            (result &&
              typeof result === "object" &&
              "errorData" in result &&
              result?.errorData &&
              typeof result?.errorData === "object" &&
              "data" in result?.errorData &&
              result?.errorData?.data &&
              typeof result?.errorData?.data === "object" &&
              "error" in result?.errorData?.data &&
              result?.errorData?.data?.error &&
              typeof result?.errorData?.data?.error === "object" &&
              "status" in result?.errorData?.data?.error &&
              Array.isArray(result?.errorData?.data?.error?.status) &&
              result?.errorData?.data?.error?.status?.join(", ")) ||
            addError ||
            `Could not ${isEdit ? "update" : "add"} session.`
          ),
          icon: "error",
          timer: 5000,
        });
      }
    } catch (error) {
      showToast({
        title: "Error",
        text: "An error occurred while submitting the form.",
        icon: "error",
        timer: 5000,
      });

      console.error("Error submitting form:", error);
    }
  };

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Updated Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">{isEdit ? "Edit" : "Add"} Session</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.sessionList}>Sessions</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEdit ? "Edit" : "Add"} Session
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          <div className="row">
            <div className="col-md-12">
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Session Core Information */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-calendar-event fs-16" />
                      </span>
                      <h4 className="text-dark">Session Details</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Session Name <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                            placeholder="Enter session name"
                            {...control.register("name")}
                          />
                          {errors.name && touchedFields.name && (
                            <div className="invalid-feedback">{errors.name.message}</div>
                          )}
                        </div>
                        <div className="mb-3">
                          <label className="form-label">Start Date <span className="text-danger">*</span></label>
                          <Controller
                            name="start_date"
                            control={control}
                            render={({ field }) => (
                              <DatePicker
                                className={`w-100 ${errors.start_date ? 'is-invalid' : ''}`}
                                format="DD-MM-YYYY"
                                {...field}
                              />
                            )}
                          />
                          {errors.start_date && touchedFields.start_date && (
                            <div className="invalid-feedback">{errors.start_date.message}</div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="mb-3">
                          <label className="form-label">Status <span className="text-danger">*</span></label>
                          <Controller
                            name="status"
                            control={control}
                            render={({ field }) => (
                              <CommonSelect
                                className="select"
                                options={status}
                                value={status.find((option) => option.value === field.value) || null}
                                onChange={(value) => field.onChange(value?.value || "")}
                              />
                            )}
                          />
                          {errors.status && touchedFields.status && (
                            <div className="invalid-feedback">{errors.status.message}</div>
                          )}
                        </div>
                        <div className="mb-3">
                          <label className="form-label">End Date <span className="text-danger">*</span></label>
                          <Controller
                            name="end_date"
                            control={control}
                            render={({ field }) => (
                              <DatePicker
                                className={`w-100 ${errors.end_date ? 'is-invalid' : ''}`}
                                format="DD-MM-YYYY"
                                {...field}
                              />
                            )}
                          />
                          {errors.end_date && touchedFields.end_date && (
                            <div className="invalid-feedback">{errors.end_date.message}</div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-12">
                        <div className="mb-3">
                          <label className="form-label">
                            Session Description <span className="text-danger">*</span>
                          </label>
                          <textarea
                            className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                            rows={4}
                            placeholder="Enter session description"
                            {...control.register("description")}
                          />
                          {errors.description && touchedFields.description && (
                            <div className="invalid-feedback">{errors.description.message}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="text-end mt-4">
                  <Link to={routes.sessionList} className="btn btn-light me-3">
                    Cancel
                  </Link>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!isDirty || isAdding || isUpdating}
                  >
                    {isAdding
                      ? "Saving..."
                      : isUpdating
                        ? "Updating..."
                        : isEdit
                          ? "Update Session"
                          : "Create Session"}
                  </button>
                </div>
              </form>
            </div>
          </div>
          {/* {addError && (
            <div className="alert alert-danger mt-3">{addError}</div>
          )} */}
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default AddSession;
